// `/accept-invite?token=…` (and `/sign-up?invite=…`; spec/dashboard.md sections 3.1 and 12.4; spec/api-conventions.md
// section 21.3): an anonymous page. `POST /v1/auth/invites:inspect` says what the invite grants and whether its email has an
// account: without one, the page is a sign-up form (name, password) that `POST /v1/auth/invites:accept` turns into the
// account, the memberships and a session in one call; with one, the caller signs in as that email first
// (`/sign-in?invite=<token>` comes back here) and accepts with the session. A dead invite shows its status.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { InviteInspect } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { routes } from '../shell/routes';
import { formatInstantWithZone, useTimeZone } from '../ui/time';
import { SignInBrand } from './SignIn';
import './org/org.css';

const STATUS_TEXT: Record<string, string> = {
  accepted: 'This invite was already accepted.',
  revoked: 'This invite was revoked. Ask the organization for a new one.',
  expired: 'This invite has expired. Ask the organization to resend it.',
};

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  return <AcceptInvite token={params.get('token') ?? ''} />;
}

export function AcceptInvite({ token }: { token: string }) {
  const { client, session, adoptSession, signOut } = useAuth();
  const navigate = useNavigate();
  const [zone] = useTimeZone();
  const [invite, setInvite] = useState<InviteInspect | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInvite(null);
    setLoadError(null);
    if (!token) return;
    client.data(ops.inspectInvite, { body: { token }, auth: false }).then(
      (r) => !cancelled && setInvite(r),
      (e: unknown) => !cancelled && setLoadError(e instanceof ApiError ? e : new ApiError(0, 'internal_error', 'Something went wrong.', '')),
    );
    return () => {
      cancelled = true;
    };
  }, [client, token]);

  const accept = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!invite) return;
    setBusy(true);
    setError(null);
    try {
      const body = invite.accountExists ? { token } : { token, displayName: displayName.trim(), password };
      const r = await client.data(ops.acceptInvite, { body, auth: invite.accountExists });
      adoptSession(r.session);
      navigate(routes.games(r.organization.slug), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'internal_error', 'Something went wrong.', ''));
    } finally {
      setBusy(false);
    }
  };

  const signInLink = `/sign-in?invite=${encodeURIComponent(token)}`;
  let body: ReactNode;
  if (!token) {
    body = (
      <p className="error" role="alert">
        This link carries no invite. Open the link from the invite exactly as it was given to you.
      </p>
    );
  } else if (loadError) {
    body = (
      <p className="error" role="alert">
        {loadError.status === 404 ? 'This invite link is not valid. Ask the organization for a new one.' : `${loadError.message}${loadError.requestId ? ` (request ${loadError.requestId})` : ''}`}
      </p>
    );
  } else if (!invite) {
    body = (
      <div className="signin-loading" role="status" aria-label="Reading the invite">
        <span className="skeleton" />
        <span className="skeleton" />
      </div>
    );
  } else {
    const sameUser = Boolean(session && session.email.toLowerCase() === invite.email.toLowerCase());
    body = (
      <>
        <div data-testid="invite-summary">
          <p className="lede">
            You are invited to <strong>{invite.organization.name}</strong> as <strong>{invite.role}</strong>, for <strong>{invite.email}</strong>.
          </p>
          {invite.games.length > 0 && (
            <ul className="invite-games" aria-label="Games">
              {invite.games.map((g) => (
                <li key={g.gameId}>
                  {g.name} — {g.role}
                </li>
              ))}
            </ul>
          )}
          {invite.accessExpiresAt && <p className="muted small">Access until {formatInstantWithZone(invite.accessExpiresAt, zone)}.</p>}
          {invite.status === 'pending' && invite.expiresAt && <p className="muted small">The invite is valid until {formatInstantWithZone(invite.expiresAt, zone)}.</p>}
        </div>
        {invite.status !== 'pending' ? (
          <p className="error" role="alert" data-testid="invite-status">
            {STATUS_TEXT[invite.status] ?? `This invite is ${invite.status}.`}
          </p>
        ) : invite.accountExists ? (
          sameUser ? (
            <form onSubmit={(e) => void accept(e)} aria-label="Accept the invite">
              <button type="submit" disabled={busy}>
                {busy ? 'Accepting…' : 'Accept the invite'}
              </button>
            </form>
          ) : (
            <>
              {session && (
                <div className="signin-session">
                  <span className="muted">You are signed in as {session.email}.</span>
                  <button type="button" onClick={() => void signOut()}>
                    Sign out
                  </button>
                </div>
              )}
              <div className="links">
                <p>
                  <Link to={signInLink}>Sign in as {invite.email} to accept</Link>
                </p>
              </div>
            </>
          )
        ) : (
          <form onSubmit={(e) => void accept(e)} aria-label="Create your account">
            <p className="lede">Create the account of {invite.email} to accept.</p>
            <label>
              Name
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" required />
              {error?.fieldMessage('displayName') && <span className="field-error">{error.fieldMessage('displayName')}</span>}
            </label>
            <label>
              Password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={10} required />
              {error?.fieldMessage('password') && <span className="field-error">{error.fieldMessage('password')}</span>}
            </label>
            <button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create account and accept'}
            </button>
          </form>
        )}
        {error && !error.details?.length && (
          <p className="error" role="alert">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </p>
        )}
      </>
    );
  }

  return (
    <div className="signin">
      <div className="card" aria-labelledby="accept-title" data-testid="accept-invite">
        <SignInBrand />
        <h1 id="accept-title">Accept the invite</h1>
        {body}
      </div>
    </div>
  );
}
